import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { ScheduleComponent } from '@syncfusion/ej2-react-schedule';
import dayjs from 'dayjs';
import { getConfig, setConfig } from '../utility_objects/WhiteCache';
import {
  FINESTRA_PREDEFINITA,
  FinestraOraria,
} from '../utility_objects/FinestraOraria';
import {
  AsseOre,
  ScheduleInterno,
  getGrigliaOre,
  leggiPixelRipristinati,
  leggiPrimaOraVisibile,
  portaPrimaOraVisibile,
} from '../utility_objects/GrigliaOreSyncfusion';

/** Calendari della console, con lo scroll iniziale ricordato per ciascuno. */
export type CalendarioScroll = 'default' | 'personal' | 'studio' | 'user';

/**
 * Chiave del localStorage con l'ora in cima alla griglia verticale del
 * calendario (es. 8.5 = le 8:30), accanto al payload in pixel di Syncfusion:
 * una per calendario, come il payload.
 */
export const getChiaveOraInCima = (calendario: CalendarioScroll): string =>
  `bb-${calendario}-schedule-ora-in-cima`;

/**
 * Chiave del localStorage con l'ora a sinistra della vista Orizzontale
 * (TimelineDay) dei giornalieri: un'altra chiave, perché Syncfusion tiene in
 * un solo payload i pixel delle due direzioni.
 */
export const getChiaveOraASinistra = (calendario: CalendarioScroll): string =>
  `bb-${calendario}-schedule-ora-a-sinistra`;

const ASSI: readonly AsseOre[] = ['verticale', 'orizzontale'];

const getChiave = (calendario: CalendarioScroll, asse: AsseOre): string =>
  asse === 'verticale'
    ? getChiaveOraInCima(calendario)
    : getChiaveOraASinistra(calendario);

export interface SchedulerPersistenceParametri {
  /** Chiave dell'ora salvata e dello scroll iniziale (hasInitialScrolled) */
  calendario: CalendarioScroll;
  selectedDate: Date;
  focus10: boolean;
  /** Ore mostrate adesso, intere */
  startHour: number;
  endHour: number;
  /**
   * Ore di sempre del calendario, 5-24 se non indicate: i pixel salvati prima
   * della chiave dell'ora valgono per queste
   */
  predefinita?: Readonly<FinestraOraria>;
  /**
   * Vista mostrata, solo per i calendari che passano fra Verticale e
   * Orizzontale senza rimontare lo Scheduler (giornalieri Studio e Utente)
   */
  view?: string;
}

// Ora salvata; null se manca o non è valida (privacy, dati vecchi)
const leggiOraSalvata = (chiave: string): number | null => {
  try {
    const salvata = window.localStorage.getItem(chiave);
    if (salvata === null || salvata === '') return null;
    const ora = Number(salvata);
    return Number.isFinite(ora) && ora >= 0 && ora <= 24 ? ora : null;
  } catch {
    return null;
  }
};

const salvaOra = (chiave: string, ora: number | null): void => {
  try {
    if (ora === null) window.localStorage.removeItem(chiave);
    else window.localStorage.setItem(chiave, String(ora));
  } catch {
    /* localStorage non disponibile: resta il payload in pixel */
  }
};

/**
 * Gestisce la persistenza e lo scroll iniziale di un calendario Syncfusion della
 * console (agenda negozio, Personale, giornalieri Studio e Utente):
 * - Override di getPersistData per salvare solo la scroll position (non eventSettings)
 * - Scroll all'ora corrente solo al primo caricamento del calendario
 * - Scroll ancorato alla prima ora visibile (in cima, o a sinistra nella vista
 *   Orizzontale), non ai pixel: con la riga Ore del riquadro Visualizzazione
 *   (Tutte | Prenotabili) le ore mostrate cambiano, e gli stessi pixel
 *   indicherebbero un'altra ora
 *
 * Syncfusion salva i pixel (enablePersistence), li rilegge al montaggio e li
 * rimette dopo ogni ridisegno della vista, anche quello dovuto a un cambio di
 * startHour o endHour. Per questo l'ora si salva su una chiave propria del
 * calendario, negli stessi momenti del payload (smontaggio e chiusura della
 * pagina), e si rimette dopo il ripristino di Syncfusion: al montaggio e dopo
 * ogni cambio delle ore mostrate. Il wrapper React applica le prop nel render
 * (shouldComponentUpdate → setProperties), quindi il ridisegno è già avvenuto
 * quando girano i layout effect di questo hook. Senza la chiave (salvataggi di
 * prima) i pixel valgono per le ore di sempre del calendario.
 *
 * Nei giornalieri la vista Verticale e l'Orizzontale hanno le ore su assi
 * diversi ma un solo payload, e si passa dall'una all'altra senza rimontare:
 * Syncfusion rimette sulle ore i pixel dell'altra vista, come prima. Lì l'ora si
 * rilegge dai pixel, senza spostare nulla, e si salva solo quella dell'asse
 * mostrato: al montaggio dopo, sull'altro asse, i pixel che Syncfusion rimette
 * non sono i suoi, e senza un'ora salvata valgono loro, come prima.
 *
 * Lo zoom invece tiene i pixel, come prima: dopo un cambio di zoom l'ora
 * all'inizio è quella che resta lì (lo scroll della griglia nuova la aggiorna).
 */
export function useSchedulerPersistence(
  scheduleObj: RefObject<ScheduleComponent | null>,
  {
    calendario,
    selectedDate,
    focus10,
    startHour,
    endHour,
    predefinita = FINESTRA_PREDEFINITA,
    view,
  }: SchedulerPersistenceParametri
) {
  const inizioPredefinito = predefinita.startHour;
  // Prima ora visibile per asse, da rimettere dopo un ridisegno; null finché
  // non si sa (montaggio in Mese o Agenda senza un'ora salvata)
  const ore = useRef<Record<AsseOre, number | null>>({
    verticale: null,
    orizzontale: null,
  });
  // Asse delle ore della vista mostrata all'ultimo commit; null nel Mese e in
  // Agenda. Il salvataggio arriva con la vista già distrutta: non c'è più DOM
  // da cui ricavarlo
  const asseMostrato = useRef<AsseOre | null>(null);
  // Ore mostrate all'ultimo commit: solo la griglia con queste ore dice l'ora
  // all'inizio (vedi onScroll)
  const oreDisegnate = useRef({ startHour, endHour });
  const vistaDisegnata = useRef(view);
  const montato = useRef(false);

  // Override getPersistData: salva solo scroll position
  useEffect(() => {
    if (scheduleObj.current) {
      try {
        const schedule = scheduleObj.current as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        const originalGetPersistData = schedule.getPersistData.bind(schedule);

        schedule.getPersistData = () => {
          let data = { scrollTop: 0, scrollLeft: 0 };
          try {
            const persistData = originalGetPersistData();
            data = JSON.parse(persistData);
          } catch (error) {
            console.error('Error in getPersistData override:', error);
          }
          // Accanto ai pixel, negli stessi momenti, l'ora: dell'asse mostrato,
          // cancellando quella dell'altro; nel Mese e in Agenda quelle che ci sono
          const asse = asseMostrato.current;
          for (const asseOre of ASSI) {
            const ora = ore.current[asseOre];
            if (asse !== null && asseOre !== asse)
              salvaOra(getChiave(calendario, asseOre), null);
            else if (ora !== null)
              salvaOra(getChiave(calendario, asseOre), ora);
          }
          return JSON.stringify({
            scrollTop: data.scrollTop ?? 0,
            scrollLeft: data.scrollLeft ?? 0,
          });
        };
      } catch (error) {
        console.error('Error overriding getPersistData:', error);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Ogni scroll della griglia, dell'utente o di Syncfusion, aggiorna l'ora
  // all'inizio, tranne quelli di una griglia con ore non ancora confermate. Lo
  // scroll non risale: si ascolta in cattura sull'elemento di Syncfusion, che
  // resta mentre le viste si ridisegnano.
  useLayoutEffect(() => {
    const schedule = scheduleObj.current as ScheduleInterno | null;
    const elemento = schedule?.element;
    if (!elemento) return;
    const onScroll = (evento: Event) => {
      const griglia = getGrigliaOre(schedule);
      if (griglia === null || evento.target !== griglia.area) return;
      // Ore diverse da quelle dell'ultimo commit: è il ridisegno di Syncfusion
      // in un render non ancora confermato (il render differito, che React
      // interrompe per cedere il thread, o un render scartato), con i pixel
      // vecchi rimessi sulle ore nuove. L'ora giusta la rimette il layout
      // effect delle ore al commit. Il confronto esatto regge: le ore arrivano
      // a Syncfusion come 'HH:00' (formatOraSyncfusion)
      const disegnate = oreDisegnate.current;
      if (
        griglia.inizio !== disegnate.startHour ||
        griglia.fine !== disegnate.endHour
      )
        return;
      ore.current[griglia.asse] = leggiPrimaOraVisibile(schedule);
    };
    elemento.addEventListener('scroll', onScroll, true);
    return () => elemento.removeEventListener('scroll', onScroll, true);
  }, [scheduleObj]);

  // Al montaggio, a ogni cambio delle ore mostrate e a ogni cambio di vista,
  // dopo il ripristino in pixel di Syncfusion
  useLayoutEffect(() => {
    // Prima delle uscite anticipate: vale al montaggio, senza un'ora da
    // rimettere e nel Mese o in Agenda. Nessuno scroll arriva durante l'effect
    oreDisegnate.current = { startHour, endHour };
    const vistaCambiata = vistaDisegnata.current !== view;
    vistaDisegnata.current = view;
    const schedule = scheduleObj.current as ScheduleInterno | null;
    const griglia = getGrigliaOre(schedule);
    asseMostrato.current = griglia?.asse ?? null;

    if (montato.current) {
      if (griglia === null) return;
      // Fra Verticale e Orizzontale: sulle ore ci sono i pixel dell'altra
      // vista, come prima. Si rilegge l'ora da lì, senza spostare nulla
      if (vistaCambiata) {
        ore.current[griglia.asse] = leggiPrimaOraVisibile(schedule);
        return;
      }
      // Ore mostrate cambiate: all'inizio l'ora che c'era
      const ora = ore.current[griglia.asse];
      if (ora === null) return;
      ore.current[griglia.asse] = portaPrimaOraVisibile(schedule, ora) ?? ora;
      return;
    }
    montato.current = true;
    // Mese o Agenda: le ore salvate restano per la prossima vista oraria
    if (griglia === null) {
      ore.current = {
        verticale: leggiOraSalvata(getChiave(calendario, 'verticale')),
        orizzontale: leggiOraSalvata(getChiave(calendario, 'orizzontale')),
      };
      return;
    }
    const salvata = leggiOraSalvata(getChiave(calendario, griglia.asse));
    // Pixel rimessi da Syncfusion al montaggio. Sull'iPad nelle viste verticali
    // non li rimette nell'area che scorre: si lascia la prima ora lavorativa,
    // come prima; senza pixel salvati (primo avvio) anche
    const pixel = leggiPixelRipristinati(schedule, griglia.asse);
    if (typeof pixel !== 'number' || !Number.isFinite(pixel)) {
      ore.current[griglia.asse] = leggiPrimaOraVisibile(schedule);
      return;
    }
    const ora = salvata ?? inizioPredefinito + pixel / griglia.pixelOra;
    ore.current[griglia.asse] = portaPrimaOraVisibile(schedule, ora) ?? ora;
  }, [scheduleObj, startHour, endHour, view, calendario, inizioPredefinito]);

  // Scroll all'ora corrente solo al primo caricamento
  useEffect(() => {
    const config = getConfig();
    if (config.hasInitialScrolled[calendario]) return;

    const { underline } = config;
    if (
      scheduleObj?.current &&
      dayjs().isSame(dayjs(selectedDate), 'day') &&
      underline === '' &&
      !focus10
    ) {
      try {
        setTimeout(() => {
          // Un'ora prima di adesso, dentro le ore mostrate in quel momento
          const schedule = scheduleObj?.current as ScheduleInterno | null;
          const griglia = getGrigliaOre(schedule);
          const ora = portaPrimaOraVisibile(
            schedule,
            dayjs().subtract(1, 'hour').hour()
          );
          if (griglia !== null && ora !== null) ore.current[griglia.asse] = ora;
          setConfig('hasInitialScrolled', {
            ...config.hasInitialScrolled,
            [calendario]: true,
          });
        }, 200);
      } catch (error) {
        /* empty */
      }
    }
  }, [calendario, focus10, scheduleObj, selectedDate]);
}
